import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  Typography,
  Avatar,
  Button,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentIcon from "@mui/icons-material/Payment";
import InfoIcon from "@mui/icons-material/Info";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const PaymentSummary = ({
  t,
  isPaid,
  isPayrollDeduct = false,
  totalAmount,
  isPartiallyPaid = false,
  paidAmount = 0,
  dueAmount = 0,
  onTogglePaymentStatus,
  isUpdatingPaymentStatus,
  disableToggle,
  errorMessage,
  onTerminalPayment,
  isProcessingTerminalPayment = false,
  onPayrollDeduct,
  isProcessingPayLater = false,
}) => {
  // Parse totalAmount to check if it's zero
  const parseAmount = (amountStr) => {
    if (!amountStr || typeof amountStr !== "string") return 0;
    const numericValue = parseFloat(amountStr.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const amountValue = parseAmount(totalAmount);
  const isZeroAmount = amountValue === 0;
  const showPartial = isPartiallyPaid && !isPaid;
  const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

  // Pay Later state: shown when payroll deduct is set AND all orders are settled
  const showPayLater = isPayrollDeduct && isPaid;

  // Determine payment status display
  const paymentStatusColor = isZeroAmount
    ? "#64748b"
    : isPaid
      ? "#10b981" // Green for paid (includes Pay Later)
      : showPartial
        ? "#f59e0b" // Orange for partially paid
        : "#ef4444"; // Red for unpaid

  const paymentStatusText = isZeroAmount
    ? t("Console.ActionPopup.NoPaymentRequired", { defaultValue: "No Payment Required" })
    : showPayLater
      ? t("Console.ActionPopup.PayLater", { defaultValue: "Pay Later" })
      : isPaid
        ? t("Console.ActionPopup.Paid")
        : showPartial
          ? t("Console.ActionPopup.PartiallyPaid", { defaultValue: "Partially Paid" })
          : t("Console.ActionPopup.AmountDue");

  const paymentStatusIcon = isZeroAmount ? (
    <InfoIcon />
  ) : showPayLater ? (
    <AccessTimeIcon />
  ) : isPaid ? (
    <CheckCircleIcon />
  ) : showPartial ? (
    <InfoIcon />
  ) : (
    <CancelIcon />
  );

  // Disable toggle when amount is zero
  const isToggleDisabled = disableToggle || isUpdatingPaymentStatus || isZeroAmount;
  // Terminal-pay and external-pay are also disabled when Pay Later is active
  const isPaymentActionDisabled = isToggleDisabled || showPayLater;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2.5,
          pb: 2,
          borderBottom: "2px solid #f1f5f9",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: paymentStatusColor,
              width: 32,
              height: 32,
            }}
          >
            <PaymentIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.1rem",
              color: "#1e293b",
            }}
          >
            {t("Console.ActionPopup.PaymentInformation", {
              defaultValue: "Payment Information",
            })}
          </Typography>
        </Box>
        {!isZeroAmount && (
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            {onTerminalPayment && (
              <Button
                variant="contained"
                size="small"
                startIcon={<CreditCardIcon />}
                onClick={onTerminalPayment}
                disabled={isProcessingTerminalPayment || isPaymentActionDisabled}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 140,
                  backgroundColor: "#10b981",
                  color: "#ffffff",
                  "&:hover": { backgroundColor: "#059669" },
                  "&:disabled": { backgroundColor: "#cbd5e1", color: "#94a3b8" },
                }}
              >
                {isProcessingTerminalPayment ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    {t("Console.ActionPopup.Processing", { defaultValue: "Processing..." })}
                  </Box>
                ) : (
                  t("Console.ActionPopup.PayOnTerminal", { defaultValue: "Pay on Terminal" })
                )}
              </Button>
            )}
            {onPayrollDeduct && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AccessTimeIcon />}
                onClick={onPayrollDeduct}
                disabled={isProcessingPayLater || isToggleDisabled}
                sx={{
                  textTransform: "none",
                  borderWidth: 2,
                  fontWeight: 600,
                  minWidth: 150,
                  ...(showPayLater
                    ? {
                        borderColor: "#f59e0b",
                        color: "#f59e0b",
                        "&:hover": {
                          borderColor: "#d97706",
                          backgroundColor: "rgba(245,158,11,0.08)",
                        },
                      }
                    : {
                        borderColor: "#ef4444",
                        color: "#ef4444",
                        "&:hover": {
                          borderColor: "#dc2626",
                          backgroundColor: "rgba(239,68,68,0.08)",
                        },
                      }),
                  "&:disabled": { borderColor: "#cbd5e1", color: "#94a3b8" },
                }}
              >
                {isProcessingPayLater ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    {t("Console.ActionPopup.Processing", { defaultValue: "Processing..." })}
                  </Box>
                ) : showPayLater ? (
                  t("Console.ActionPopup.CancelPayLater", { defaultValue: "Cancel Pay Later" })
                ) : (
                  t("Console.ActionPopup.PayLaterButton", { defaultValue: "Pay Later" })
                )}
              </Button>
            )}
            {onTogglePaymentStatus && (
              <Button
                variant="outlined"
                size="small"
                onClick={onTogglePaymentStatus}
                disabled={isPaymentActionDisabled}
                sx={{
                  textTransform: "none",
                  borderWidth: 2,
                  borderColor: "#7c3aed",
                  color: "#7c3aed",
                  fontWeight: 600,
                  minWidth: 150,
                  "&:hover": {
                    borderColor: "#6d28d9",
                    backgroundColor: "rgba(124, 58, 237, 0.08)",
                  },
                  "&:disabled": { borderColor: "#cbd5e1", color: "#94a3b8" },
                }}
              >
                {isUpdatingPaymentStatus ? (
                  <CircularProgress size={18} color="inherit" />
                ) : isPaid ? (
                  t("Console.ActionPopup.MarkAsUnpaid", { defaultValue: "Mark as Unpaid" })
                ) : (
                  t("Console.ActionPopup.MarkAsPaid", { defaultValue: "Pay Externally" })
                )}
              </Button>
            )}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          p: 2.5,
          backgroundColor: isZeroAmount
            ? "#f1f5f9"
            : isPaid
              ? "#f0fdf4" // Green tint for paid (includes Pay Later)
              : showPartial
                ? "#fffbeb" // Amber tint for partially paid
                : "#fef2f2",
          borderRadius: 2,
          border: `2px solid ${paymentStatusColor}`,
          boxShadow: `0 2px 8px ${paymentStatusColor}20`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Chip
            icon={paymentStatusIcon}
            label={paymentStatusText}
              sx={{
              backgroundColor: isZeroAmount
                ? "#e2e8f0"
                : isPaid
                  ? "#d1fae5" // Green chip background (includes Pay Later)
                  : showPartial
                    ? "#fef3c7" // Amber chip background for partially paid
                    : "#fee2e2",
              color: paymentStatusColor,
              fontWeight: 700,
              fontSize: "0.875rem",
              px: 2,
              py: 1,
              height: "auto",
              "& .MuiChip-icon": {
                color: paymentStatusColor,
                fontSize: "1.25rem",
              },
            }}
          />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.875rem",
              color: "#64748b",
            }}
          >
            {showPartial
              ? t("Console.ActionPopup.AmountDue", { defaultValue: "Amount Due" })
              : `${t("Console.ActionPopup.AmountTotal")}:`}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.75rem",
            color: paymentStatusColor,
            letterSpacing: "-0.02em",
          }}
        >
          {totalAmount}
        </Typography>
      </Box>
      {showPartial ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mt: 1.5,
            px: 2.5,
            py: 1.5,
            backgroundColor: "#fffbeb",
            borderRadius: 2,
            border: "1px dashed #f59e0b",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <CheckCircleIcon sx={{ fontSize: 18, color: "#10b981" }} />
            <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#475569" }}>
              {t("Console.ActionPopup.AmountPaid", { defaultValue: "Paid" })}:
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#10b981" }}>
              {formatMoney(paidAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <CancelIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
            <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#475569" }}>
              {t("Console.ActionPopup.AmountRemaining", { defaultValue: "Remaining" })}:
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#f59e0b" }}>
              {formatMoney(dueAmount)}
            </Typography>
          </Box>
        </Box>
      ) : null}
      {errorMessage ? (
        <Typography
          sx={{
            mt: 1.5,
            color: "#dc2626",
            fontSize: "0.85rem",
            fontWeight: 500,
          }}
        >
          {errorMessage}
        </Typography>
      ) : null}
    </Box>
  );
};

PaymentSummary.propTypes = {
  t: PropTypes.func.isRequired,
  isPaid: PropTypes.bool,
  isPayrollDeduct: PropTypes.bool,
  totalAmount: PropTypes.string,
  isPartiallyPaid: PropTypes.bool,
  paidAmount: PropTypes.number,
  dueAmount: PropTypes.number,
  onTogglePaymentStatus: PropTypes.func,
  isUpdatingPaymentStatus: PropTypes.bool,
  disableToggle: PropTypes.bool,
  errorMessage: PropTypes.string,
  onTerminalPayment: PropTypes.func,
  isProcessingTerminalPayment: PropTypes.bool,
  onPayrollDeduct: PropTypes.func,
  isProcessingPayLater: PropTypes.bool,
};

PaymentSummary.defaultProps = {
  isPaid: false,
  isPayrollDeduct: false,
  totalAmount: "$0.00",
  isPartiallyPaid: false,
  paidAmount: 0,
  dueAmount: 0,
  onTogglePaymentStatus: undefined,
  isUpdatingPaymentStatus: false,
  disableToggle: false,
  errorMessage: "",
  onTerminalPayment: undefined,
  isProcessingTerminalPayment: false,
  onPayrollDeduct: undefined,
  isProcessingPayLater: false,
};

export default PaymentSummary;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/components/PaymentSummary.jsx